/**
 * Exposure Alert Categories - UI metadata for port/protocol groupings.
 *
 * Categories are display-only metadata. The backend (pkg/lib/exposure/) is the
 * source of truth for port mappings. Settings are stored as a single
 * `exposure_settings` object via the settings API.
 *
 * Used in:
 * - CategoryExposureAlertsForm
 * - ExposureAlertsCard (category summary display)
 */

/**
 * Risk level for exposure categories.
 * Used for visual styling and priority sorting.
 */
export type ExposureCategoryRisk = 'critical' | 'high' | 'medium';

/**
 * Category definition for exposure alerts.
 * Groups related ports/protocols by security intent.
 */
export interface ExposureCategory {
  /** Unique identifier for the category */
  id: string;
  /** Display name for the category */
  name: string;
  /** Brief description listing example services */
  description: string;
  /** Ports associated with this category */
  ports: number[];
  /** Protocols associated with this category */
  protocols: string[];
  /** Risk level for visual styling */
  risk: ExposureCategoryRisk;
  /** Whether category is recommended for most users */
  recommended: boolean;
}

/**
 * Security categories for exposure alert selection.
 * Ordered by risk level (critical first) and recommendation status.
 */
export const EXPOSURE_CATEGORIES: ExposureCategory[] = [
  {
    id: 'remote-access',
    name: 'Remote Access Services',
    description: 'SSH, RDP, VNC, Telnet',
    ports: [22, 3389, 5900, 23],
    protocols: ['ssh', 'rdp', 'vnc', 'telnet'],
    risk: 'critical',
    recommended: true,
  },
  {
    id: 'databases',
    name: 'Databases Exposed to Internet',
    description:
      'MySQL, PostgreSQL, MongoDB, Redis, MSSQL, Oracle, Elasticsearch, Cassandra, CouchDB, Memcached',
    ports: [3306, 5432, 27017, 6379, 1433, 1521, 9200, 9300, 9042, 5984, 11211],
    protocols: [
      'mysql',
      'postgresql',
      'mongodb',
      'redis',
      'mssql',
      'oracle-tns',
      'elasticsearch',
      'cassandra',
      'couchdb',
      'memcached',
    ],
    risk: 'critical',
    recommended: true,
  },
  {
    id: 'file-sharing',
    name: 'File Sharing & SMB Services',
    description: 'SMB, NetBIOS, FTP, NFS, TFTP',
    ports: [445, 137, 138, 139, 21, 2049, 69],
    protocols: ['smb', 'ftp', 'nfs', 'tftp'],
    risk: 'high',
    recommended: true,
  },
  {
    id: 'management',
    name: 'Management Interfaces',
    description: 'SSH, RDP, Telnet, SNMP, VNC, WinRM, IPMI',
    ports: [22, 3389, 23, 161, 162, 5900, 5985, 5986, 623],
    protocols: ['ssh', 'rdp', 'telnet', 'snmp', 'vnc', 'winrm', 'ipmi'],
    risk: 'high',
    recommended: true,
  },
  {
    id: 'telecom',
    name: 'Telecom Protocols',
    description: 'RADIUS, SIP, GTP, Diameter, SIGTRAN, H.323',
    ports: [1812, 1813, 5060, 5061, 2123, 2152, 3868, 2905, 1720],
    protocols: ['radius', 'sip', 'gtp', 'diameter', 'sigtran', 'h323'],
    risk: 'high',
    recommended: false,
  },
];

/**
 * Gets category by ID.
 */
export function getCategoryById(id: string): ExposureCategory | undefined {
  return EXPOSURE_CATEGORIES.find(c => c.id === id);
}

/**
 * Gets recommended categories (for initial setup or suggestions).
 */
export function getRecommendedCategories(): ExposureCategory[] {
  return EXPOSURE_CATEGORIES.filter(c => c.recommended);
}

/**
 * Severity order for sorting categories.
 * Critical risks should appear first, followed by high, then medium.
 */
const SEVERITY_ORDER: Record<ExposureCategoryRisk, number> = {
  critical: 0,
  high: 1,
  medium: 2,
};

/**
 * Gets all categories sorted by severity (critical first, then high, then medium).
 * This provides a consistent, priority-based ordering for display.
 *
 * @returns Categories sorted by risk severity
 */
export function getCategoriesBySeverity(): ExposureCategory[] {
  return [...EXPOSURE_CATEGORIES].sort(
    (a, b) => SEVERITY_ORDER[a.risk] - SEVERITY_ORDER[b.risk]
  );
}
